import { useEffect } from 'react';

import { ExternalLinkIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import Layout from '@/components/templates/Layout';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import PermissionsWrapper from '@/components/templates/PermissionsWrapper';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import useDesks from '@/hooks/useDesks';
import RulesHeader from '@/modules/rules/components/RulesHeader/RulesHeader';
import { selectAccountSlug } from '@/redux/session/selectors';
import { ENVIRONMENTS_DOCS_URL } from '@/util/constants';

export const RulesNoEnvironment = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { desks, listStatus, handleCreateDesk } = useDesks();

  const slug = useSelector(selectAccountSlug);
  const { slug: urlSlug } = useParams();

  useEffect(() => {
    if (slug === urlSlug && listStatus === 'success' && desks.length > 0) {
      navigate(`/${urlSlug}/environments/${desks[0].desk_id}/rules`, { replace: true });
    }
  }, [desks, listStatus, slug, urlSlug, navigate]);

  // Don't render until we confirm there are no desks
  if (slug !== urlSlug || listStatus !== 'success' || desks.length > 0) {
    return null;
  }

  return (
    <Layout>
      <PermissionsWrapper module="rules">
        <RulesHeader />
        <PageContentWrapper>
          <Empty>
            <EmptyHeader>
              <EmptyMedia>
                <img src="/assets/empty-states/ai-agents.webp" alt="" />
              </EmptyMedia>
              <EmptyTitle>{t('rules.no_environment.title')}</EmptyTitle>
              <EmptyDescription>{t('rules.no_environment.description')}</EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button size="sm" onClick={handleCreateDesk}>
                <PlusIcon className="h-4 w-4" />
                {t('common.create')}
              </Button>

              <a
                href={ENVIRONMENTS_DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:underline"
              >
                <span className="text-sm leading-6 font-medium">{t('common.learn_more')}</span>
                <ExternalLinkIcon className="h-4 w-4 text-foreground-muted" />
              </a>
            </EmptyContent>
          </Empty>
        </PageContentWrapper>
      </PermissionsWrapper>
    </Layout>
  );
};
